"use client";

import { ArrowRight, ArrowUpRight, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Referral() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.1
  });
  const {
    ref: bodyRef,
    isVisible: bodyVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const descriptors = ["— For brokers, realtors, capital partners, real estate developers, general contractors, credit repair agencies, and other platforms or networks that work with real estate investors.", "— We welcome connectors and affiliates of all kinds nationwide.", "— Referrals are two‑way: we send business to our partners just as they send investors to us."];
  return <section data-section-id="5287" id="engine-03-the-referral-engine" className="relative min-h-screen w-full overflow-hidden bg-background py-32 md:py-40 lg:py-[200px]">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-10">
          {}
          <div className="lg:col-span-6 flex flex-col justify-center">
            {}
            <div className={`mb-10 transition-all duration-700 ease-out ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <span className="font-mono text-slate-muted uppercase" style={{
              fontSize: "11px",
              letterSpacing: "0.2em"
            }}>
                Engine 03 — Referral Network
              </span>
            </div>

            {}
            <h1 ref={headlineRef} className="font-default font-bold text-blueprint-white" style={{
            fontSize: "clamp(72px, 9vw, 130px)",
            lineHeight: "0.95",
            letterSpacing: "-0.02em"
          }}>
              <span className="block overflow-hidden">
                <span className="block transition-transform duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)]" style={{
                transform: headlineVisible ? "translateY(0)" : "translateY(110%)",
                transitionDelay: "100ms"
              }}>
                  Scale
                </span>
              </span>
              <span className="block overflow-hidden">
                <span className="block transition-transform duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)]" style={{
                transform: headlineVisible ? "translateY(0)" : "translateY(110%)",
                transitionDelay: "250ms"
              }}>
                  your{" "}
                  <span className="italic" style={{
                  color: "var(--gold-accent)"
                }}>
                    influence.
                  </span>
                </span>
              </span>
            </h1>

            {}
            <div ref={bodyRef} className={`mt-10 max-w-md transition-all duration-700 ease-out ${bodyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            transitionDelay: "500ms"
          }}>
              <p className="font-default text-slate-muted" style={{
              fontSize: "18px",
              lineHeight: "1.6"
            }}>
                We empower industry professionals to monetize their network by connecting real estate investors with professional‑grade capital solutions.
              </p>
            </div>

            {}
            <ul className="mt-[50px] space-y-3">
              {descriptors.map((descriptor, i) => <li data-index={i} key={i} className={`font-default italic text-blueprint-white/80 transition-all duration-700 ease-out ${bodyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
              fontSize: "18px",
              transitionDelay: `${650 + i * 120}ms`
            }}>
                  <span className="inline-block border-b border-blueprint-white/30 pb-1">{descriptor}</span>
                </li>)}
            </ul>

            {}
            <div className={`mt-12 flex flex-wrap items-center gap-6 transition-all duration-700 ease-out ${bodyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: "1050ms"
          }}>
              <Button asChild size="lg" className="px-7 py-6 font-default text-base shadow-md hover:shadow-lg transition-shadow rounded-lg" style={{
              backgroundColor: "var(--gold-accent)",
              color: "var(--vault-deep-blue)"
            }}>
                <Link to="/PreQualificationFormPage">
                  Join Our Referral Network
                  <ChevronDown className="ml-1 h-4 w-4" />
                </Link>
              </Button>

              <Link to="/TheBeneWealthEdgePage" className="inline-flex items-center gap-2 font-default text-blueprint-white hover:text-gold-accent transition-colors group">
                <span className="border-b border-blueprint-white/40 pb-0.5">Why BeneWealth</span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-6 relative lg:-mt-10">
            <div ref={imageRef} className={`relative transition-all duration-1000 ease-out ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
              {}
              <div className="absolute -top-8 right-2 z-10 opacity-70">
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none" stroke="var(--gold-accent)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M32 8 C32 20, 32 40, 32 56" />
                  <ellipse cx="22" cy="20" rx="8" ry="4" transform="rotate(-30 22 20)" />
                  <ellipse cx="42" cy="22" rx="8" ry="4" transform="rotate(30 42 22)" />
                  <ellipse cx="20" cy="34" rx="8" ry="4" transform="rotate(-30 20 34)" />
                  <ellipse cx="44" cy="36" rx="8" ry="4" transform="rotate(30 44 36)" />
                  <ellipse cx="24" cy="48" rx="7" ry="3.5" transform="rotate(-30 24 48)" />
                </svg>
              </div>

              {}
              <div className="relative overflow-hidden shadow-xl rounded-lg" style={{
              aspectRatio: "4 / 5"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/referral_partner_network.png" alt="Professional network collaboration in a modern architectural office" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" style={{
                filter: "sepia(0.08) saturate(1.05)"
              }} />
                {}
                <div className="absolute inset-0 pointer-events-none" style={{
                background: "linear-gradient(180deg, hsla(222, 47%, 7%, 0.2) 0%, hsla(222, 47%, 7%, 0.6) 100%)"
              }} />
              </div>

              <div className="mt-8 flex justify-start">
                <Button asChild variant="ghost" className="group h-auto px-0 py-2 text-gold-accent hover:bg-transparent transition-all rounded-lg">
                  <Link to="/PreQualificationFormPage" className="flex items-center gap-3">
                    <span className="font-default text-gold-accent text-lg border-b border-gold-accent pb-0.5 transition-colors group-hover:text-gold-accent/80 group-hover:border-gold-accent/80">
                      Join Our Referral Network
                    </span>
                    <ArrowUpRight className="h-5 w-5 text-gold-accent transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-gold-accent/80" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3">
        <span className="font-mono uppercase text-slate-muted" style={{
        fontSize: "10px",
        letterSpacing: "0.25em"
      }}>
          Scroll
        </span>
        <div className="relative h-16 w-px overflow-hidden bg-slate-muted/15">
          <span className="absolute left-0 top-0 h-1/2 w-full bg-gold-accent" style={{
          animation: "scrollLine 2.2s cubic-bezier(0.65, 0, 0.35, 1) infinite"
        }} />
        </div>
      </div>

      <style>{`
        @keyframes scrollLine {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(200%); }
        }
      `}</style>
    </section>;
}